import { router, useFocusEffect } from "expo-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import { Display, mq } from "react-native-unistyles";

import Button, { ButtonGroup } from "$src/core/components/atoms/Button/Button";
import Markdown from "$src/core/components/atoms/Markdown/Markdown";
import type { ModalRef } from "$src/core/components/atoms/Modal/types";
import ScrollView from "$src/core/components/atoms/ScrollView/ScrollView";
import Text from "$src/core/components/atoms/Text/Text";
import ContentModal from "$src/core/components/molecules/ContentModal/ContentModal";
import Debug from "$src/core/components/organisms/Debug/Debug";
import ProfileSelectionTeaser from "$src/core/components/organisms/ProfileSelectionTeaser/ProfileSelectionTeaser";
import SearchSettingsPanel from "$src/core/components/organisms/SearchSettingsPanel/SearchSettingsPanel";
import SubscriptionOffersPanel from "$src/core/components/organisms/SubscriptionOffersPanel";
import useAnalytics from "$src/core/hooks/use-analytics";
import useAuth from "$src/core/hooks/use-auth";
import useProfileMenu from "$src/core/hooks/use-profile-menu";
import useTabBarHeight from "$src/core/hooks/use-tab-bar-height";
import useTranslation from "$src/core/hooks/use-translation";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import LoadingPage from "$src/core/pages/LoadingPage/LoadingPage";
import useAboutQuery from "$src/core/queries/use-about-query";
import useSubscriptionOffersQuery from "$src/core/queries/use-subscription-offers-query";
import Alerter from "$src/core/services/alerting";
import $pageInsets from "$src/core/styles/page";

import $ from "./ProfilePage.web.styles";

const Content = ({ id, content }: { id?: string; content?: string }) => {
  if (id === "search") {
    return <SearchSettingsPanel />;
  }

  if (id === "subscription-offers") {
    return <SubscriptionOffersPanel />;
  }

  if (id === "debug") {
    return <Debug />;
  }

  return <>{content && <Markdown content={content} />}</>;
};

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { profile, logout, status: authStatus } = useAuth();
  const tabBarHeight = useTabBarHeight();

  useSubscriptionOffersQuery({
    enabled: authStatus === "authenticated",
  });

  const {
    status: aboutStatus,
    error: aboutError,
    refetch: refetchAbout,
  } = useAboutQuery();

  const items = useProfileMenu();

  const contentModalRef = useRef<ModalRef | null>(null);

  const flatItems = useMemo(
    () => items.flatMap((group) => group.items),
    [items],
  );

  const [selectedId, setSelectedId] = useState<string | null>(
    flatItems.at(0)?.id ?? null,
  );

  const selectedItem = useMemo(
    () => flatItems.find((item) => item.id === selectedId),
    [flatItems, selectedId],
  );

  const handleItemPress = useCallback((id: string) => {
    setSelectedId(id);
    contentModalRef.current?.present();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      router.dismissAll();
      router.replace("/");
      trackEvent("logout", { result: "successful" });
    } catch {
      trackEvent("logout", { result: "error" });
      Alerter.error(t("ERROR/AUTH/SIGN_OUT"));
    }
  }, [logout, trackEvent, t]);

  useFocusEffect(
    useCallback(() => {
      if (!selectedId && flatItems.at(0)?.id) {
        setSelectedId(flatItems.at(0)?.id ?? null);
      }
    }, [flatItems, selectedId]),
  );

  const MenuContent = useMemo(
    () => (
      <View style={$.menu}>
        <ProfileSelectionTeaser profile={profile} />

        <View style={$.menuItems}>
          <Display mq={mq.only.width("lg")}>
            {items.map((group) => (
              <View key={group.title}>
                <Text
                  preset="heading4"
                  accessibilityRole="header"
                  style={$.menuGroupTitle}
                >
                  {group.title}
                </Text>
                <ButtonGroup direction="column">
                  {group.items.map((item) => (
                    <Button
                      key={item.id}
                      name={item.id}
                      preset={
                        item.id === selectedId ? "primary" : "transparent"
                      }
                      align="left"
                      label={item.title}
                      icon={item.icon}
                      onPress={() => handleItemPress(item.id)}
                    />
                  ))}
                </ButtonGroup>
              </View>
            ))}
          </Display>
          <Display mq={mq.only.width(undefined, "lg")}>
            {items.map((group) => (
              <View key={group.title}>
                <Text
                  preset="heading4"
                  accessibilityRole="header"
                  style={$.menuGroupTitle}
                >
                  {group.title}
                </Text>
                <ButtonGroup direction="column">
                  {group.items.map((item) => (
                    <Button
                      key={item.id}
                      name={item.id}
                      preset="transparent"
                      align="left"
                      label={item.title}
                      icon={item.icon}
                      onPress={() => handleItemPress(item.id)}
                    />
                  ))}
                </ButtonGroup>
              </View>
            ))}
          </Display>
        </View>

        <View style={$.menuFooter}>
          {profile && (
            <Button
              preset="primary"
              name="logout"
              label={t("AUTH/SIGN_OUT")}
              icon="sign-out"
              onPress={handleLogout}
            />
          )}
        </View>
      </View>
    ),
    [items, selectedId, handleItemPress, handleLogout, profile, t],
  );

  if (aboutStatus === "error") {
    return (
      <ErrorPage
        title={t("ERROR/PROFILE_PAGE/TITLE")}
        subtitle={t("ERROR/PROFILE_PAGE/SUBTITLE")}
        ctaText={t("COMMON/RETRY")}
        ctaIcon="retry"
        onPressCta={refetchAbout}
        error={aboutError}
      />
    );
  }

  if (aboutStatus === "pending") {
    return <LoadingPage />;
  }

  return (
    <>
      {/* Large screen */}
      <Display mq={mq.only.width("lg")}>
        <View
          style={[
            $.container,
            $pageInsets.vertical({ tabBarHeight }),
            $pageInsets.horizontal,
          ]}
        >
          {MenuContent}
          <View style={$.content}>
            {selectedItem?.title && (
              <Text preset="heading1" accessibilityRole="header">
                {selectedItem.title}
              </Text>
            )}
            <Content
              id={selectedId ?? undefined}
              content={selectedItem?.content}
            />
          </View>
        </View>
      </Display>

      {/* Small screen */}
      <Display mq={mq.only.width(undefined, "lg")}>
        <ScrollView
          contentContainerStyle={[
            $.container,
            $pageInsets.vertical({ tabBarHeight }),
            $pageInsets.horizontal,
          ]}
          showsVerticalScrollIndicator={false}
          accessibilityRole="list"
        >
          {MenuContent}
          <ContentModal ref={contentModalRef} title={selectedItem?.title}>
            <Content
              id={selectedId ?? undefined}
              content={selectedItem?.content}
            />
          </ContentModal>
        </ScrollView>
      </Display>
    </>
  );
};

export default ProfilePage;
